import * as React from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  ENGAGEMENT_MODULES,
  ModuleCards,
  SELECTION_LOCK_REASON,
  type EngagementModuleKey,
} from '@/components/engagement/module-cards';
import {
  useUpdateEngagementDetailsMutation,
  type UpdateEngagementDetailsInput,
} from '@/lib/api/engagements';

/**
 * Edit engagement details — the only place the overview's Go-Live, Phase
 * and Main POC tiles can be changed after the new-engagement wizard.
 *
 * Before this modal those three tiles were frozen at whatever the wizard
 * captured: `planned_end` and `client_pm` had no edit path, and nothing
 * ever moved `status` from `setup` to `active`, so every engagement read
 * "Setup" forever. This form PATCHes `/api/v1/projects/:id` with only the
 * fields the user changed.
 *
 * Status → Phase mapping (pure function in the API, `derivePhasePathPure`):
 *   setup    → Setup
 *   active   → Discovery, or Execution once a snapshot exists
 *   archived → Closeout (all steps done)
 * The helper text under the select spells that out so "Active" isn't a
 * surprise when the tile then reads "Discovery".
 *
 * Modules: the five module switches, as the wizard's cards (shared
 * `ModuleCards`, compact rows). Until this section there was no way to
 * change modules after creation, so Discovery (never offered by the
 * wizard) could not be reached at all. Only the flags the user changed go
 * in `modules`. The API's rules are mirrored, not replaced:
 *   - Discovery on turns Selection on. Here Selection shows ticked and
 *     locked while Discovery is ticked; it is not sent, the API sets it.
 *   - Selection cannot go off while Discovery is on (the lock above).
 *   - At least one module must stay on (Save is disabled with the reason).
 * The sidebar lists Discovery and Selection from the portfolio's
 * `modules_enabled`, which the mutation invalidates on success.
 *
 * Auth: the API requires engagement lead or firm admin; a consultant's
 * save surfaces the API's authorization error inline.
 *
 * Shell (scrim, rounded-modal, shadow-modal, amber icon tile, ghost +
 * amber buttons) matches the other confirm-style modals; tokens only.
 */

type EngagementStatus = 'setup' | 'active' | 'archived';

const MODULE_KEYS: ReadonlyArray<EngagementModuleKey> = ENGAGEMENT_MODULES.map((m) => m.key);

type ModuleFlags = Record<EngagementModuleKey, boolean>;

function flagsFrom(modulesEnabled: ReadonlyArray<string>): ModuleFlags {
  return Object.fromEntries(MODULE_KEYS.map((k) => [k, modulesEnabled.includes(k)])) as ModuleFlags;
}

const STATUS_HELP: Record<EngagementStatus, string> = {
  setup: 'Phase shows Setup. Use while the baseline is still being formed.',
  active: 'Phase shows Discovery, then Execution once the first snapshot is captured.',
  archived: 'Phase shows Closeout. The engagement is finished.',
};

export interface EditEngagementDetailsModalProps {
  engagementId: string;
  projectId: string;
  initial: {
    status: string;
    start_date: string | null;
    planned_end: string | null;
    client_pm: string | null;
    /** The engagement's `modules_enabled` (API module names). */
    modules_enabled: ReadonlyArray<string>;
  };
  onClose: () => void;
}

const FIELD_CLASS =
  'rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body-sm text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

export function EditEngagementDetailsModal({
  engagementId,
  projectId,
  initial,
  onClose,
}: EditEngagementDetailsModalProps) {
  const update = useUpdateEngagementDetailsMutation(projectId, engagementId);

  const initialStatus: EngagementStatus =
    initial.status === 'active' || initial.status === 'archived' ? initial.status : 'setup';
  const [status, setStatus] = React.useState<EngagementStatus>(initialStatus);
  const [startDate, setStartDate] = React.useState(initial.start_date ?? '');
  const [plannedEnd, setPlannedEnd] = React.useState(initial.planned_end ?? '');
  const [clientPm, setClientPm] = React.useState(initial.client_pm ?? '');
  const initialModules = React.useMemo(
    () => flagsFrom(initial.modules_enabled),
    [initial.modules_enabled],
  );
  // What the user ticked. Selection as shown is `selection || discovery`.
  const [modules, setModules] = React.useState<ModuleFlags>(initialModules);
  const shownModules: ModuleFlags = {
    ...modules,
    selection: modules.selection || modules.discovery,
  };
  const noModules = MODULE_KEYS.every((k) => !shownModules[k]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !update.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, update.isPending]);

  // Send only what changed; '' on a date / PM means "clear" → null.
  const patch = React.useMemo<UpdateEngagementDetailsInput>(() => {
    const out: UpdateEngagementDetailsInput = {};
    if (status !== initialStatus) out.status = status;
    if (startDate !== (initial.start_date ?? '')) out.start_date = startDate || null;
    if (plannedEnd !== (initial.planned_end ?? '')) out.planned_end = plannedEnd || null;
    if (clientPm.trim() !== (initial.client_pm ?? '')) out.client_pm = clientPm.trim() || null;
    const changedModules = MODULE_KEYS.filter((k) => modules[k] !== initialModules[k]);
    if (changedModules.length > 0) {
      out.modules = Object.fromEntries(changedModules.map((k) => [k, modules[k]]));
    }
    return out;
  }, [status, startDate, plannedEnd, clientPm, modules, initial, initialStatus, initialModules]);

  const dirty = Object.keys(patch).length > 0;
  // Turning Discovery on starts it (framework, plan, interviews): 5 to 10 seconds.
  const settingUpDiscovery = update.isPending && patch.modules?.discovery === true;
  const dateOrderInvalid = Boolean(startDate && plannedEnd && plannedEnd < startDate);
  const invalid = dateOrderInvalid || noModules;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dirty || invalid || update.isPending) return;
    update.mutate(patch, { onSuccess: onClose });
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-scrim p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-engagement-title"
    >
      <form
        onSubmit={onSubmit}
        className="max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-modal bg-paper-0 p-5 shadow-modal"
      >
        <div className="flex items-start gap-3">
          <span className="rounded-card bg-accent-amber-100 p-2 text-accent-amber-700">
            <Pencil className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <h2 id="edit-engagement-title" className="text-h3 font-semibold text-ink-900">
                Edit engagement details
              </h2>
              <p className="text-meta text-ink-500">
                These drive the Go-Live, Phase and Main POC tiles on the overview, and which modules
                the engagement shows.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="eng-status" className="text-meta text-ink-500">
                Status
              </Label>
              <select
                id="eng-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as EngagementStatus)}
                className={FIELD_CLASS}
              >
                <option value="setup">Setup</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
              <p className="text-meta text-ink-500">{STATUS_HELP[status]}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <Label htmlFor="eng-start" className="text-meta text-ink-500">
                  Start date
                </Label>
                <input
                  id="eng-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={FIELD_CLASS}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="eng-golive" className="text-meta text-ink-500">
                  Planned go-live
                </Label>
                <input
                  id="eng-golive"
                  type="date"
                  value={plannedEnd}
                  onChange={(e) => setPlannedEnd(e.target.value)}
                  aria-invalid={dateOrderInvalid}
                  className={FIELD_CLASS}
                />
              </div>
            </div>
            {dateOrderInvalid ? (
              <p className="text-meta text-posture-critical">
                Planned go-live must be on or after the start date.
              </p>
            ) : null}

            <div className="flex flex-col gap-1">
              <Label htmlFor="eng-client-pm" className="text-meta text-ink-500">
                Main point of contact (client)
              </Label>
              <input
                id="eng-client-pm"
                type="text"
                value={clientPm}
                maxLength={200}
                onChange={(e) => setClientPm(e.target.value)}
                placeholder="Name of the client's main contact"
                className={FIELD_CLASS}
              />
            </div>

            <fieldset className="flex flex-col gap-1">
              <legend className="mb-1 text-meta text-ink-500">Modules</legend>
              <ModuleCards
                compact
                modules={MODULE_KEYS}
                selected={shownModules}
                locked={modules.discovery ? { selection: SELECTION_LOCK_REASON } : undefined}
                disabled={update.isPending}
                onToggle={(key, next) => setModules((m) => ({ ...m, [key]: next }))}
              />
              <p className="text-meta text-ink-500">
                Turning Discovery on also turns Selection on. Turning a module off hides it; its
                data is kept.
              </p>
              {noModules ? (
                <p className="text-meta text-posture-critical">At least one module must stay on.</p>
              ) : null}
            </fieldset>

            {update.error ? <ErrorMessage error={update.error} /> : null}
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          {settingUpDiscovery ? (
            <p role="status" className="mr-auto text-meta text-ink-500">
              Setting up Discovery…
            </p>
          ) : null}
          <Button type="button" variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!dirty || invalid || update.isPending}
            className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
          >
            {update.isPending ? (
              <>
                <Spinner /> Saving…
              </>
            ) : (
              'Save details'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
